<template>
  <div class="usage-report-view">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="ActivityIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Measurement') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Usage') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center flex-wrap mt-1 mt-lg-0"
        style="gap: 8px;"
      >
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="state === ReportState.LOADING"
          @click="applyFilters"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': state === ReportState.LOADING }"
          />
        </b-button>

        <b-dropdown
          variant="outline-secondary"
          size="sm"
          boundary="viewport"
          menu-class="column-selector-menu"
          no-caret
        >
          <template #button-content>
            <feather-icon
              icon="ColumnsIcon"
              size="14"
              class="mr-50"
            />
            <span class="d-none d-sm-inline">{{ $t('Columns') }}</span>
          </template>

          <div
            class="px-1 py-75"
            @click.stop
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small class="text-muted font-weight-bold">{{ $t('Select Columns') }}</small>
              <div>
                <b-button
                  size="sm"
                  variant="link"
                  class="p-0 text-primary"
                  @click="selectAllColumns"
                >
                  {{ $t('All') }}
                </b-button>
                <span class="mx-50">|</span>
                <b-button
                  size="sm"
                  variant="link"
                  class="p-0 text-primary"
                  @click="deselectAllColumns"
                >
                  {{ $t('None') }}
                </b-button>
              </div>
            </div>

            <div class="column-options">
              <b-form-checkbox-group
                v-model="selectedColumns"
                stacked
              >
                <b-form-checkbox
                  v-for="column in availableColumns"
                  :key="column.key"
                  :value="column.key"
                  class="mb-50"
                >
                  <span class="column-option-text">{{ column.label }}</span>
                </b-form-checkbox>
              </b-form-checkbox-group>
            </div>

            <hr class="my-75">
            <div class="text-center">
              <small class="text-muted">
                {{ selectedColumns.length }} {{ $t('of') }} {{ availableColumns.length }} {{ $t('columns selected') }}
              </small>
            </div>
          </div>
        </b-dropdown>

        <b-button
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('Export Sheet')"
          :disabled="isExporting"
          @click="exportToExcel"
        >
          <b-spinner
            v-if="isExporting"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Export Sheet') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'fleet-dashboard' }"
        >
          <feather-icon
            icon="BarChart2Icon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Comparative') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'maintenance-time' }"
        >
          <feather-icon
            icon="ClockIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Physical Availability') }}</span>
        </b-button>
      </div>
    </div>

    <transition name="slide-fade">
      <b-card
        v-if="showFilters"
        no-body
        class="mb-1 shadow-sm border-0 filters-card"
      >
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="text-primary mr-50"
            />
            <h6 class="mb-0 font-weight-bolder">
              {{ $t('Filters') }}
            </h6>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ $t('{count} active', { count: activeFiltersCount }) }}
            </b-badge>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              :title="$t('Clear Filters')"
              @click="resetFilters"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Close Filters')"
              @click="showFilters = false"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <div
            class="mb-1 d-flex flex-wrap align-items-center"
            style="gap: 6px;"
          >
            <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
            <b-badge
              v-for="preset in periodPresets"
              :key="preset.id"
              href="javascript:void(0)"
              :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset(preset.id)"
            >
              {{ preset.label }}
            </b-badge>
          </div>

          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Start Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="flatpickrConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('End Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="flatpickrConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              lg="6"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="UserIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Customer') }}</span>
              </label>
              <div class="d-flex">
                <vue-autosuggest
                  v-model="searchCustomer"
                  class="flex-fill"
                  :suggestions="filteredOptions"
                  :input-props="inputProps"
                  @selected="selectHandler"
                  @input="onInputChange"
                >
                  <template slot-scope="{suggestion}">
                    <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                    <div v-show="suggestion.item.ownerName">
                      <span class="text-muted">{{ $t('Belongs to: ') }}</span>
                      <span class="font-weight-bold"> {{ suggestion.item.ownerName }} </span>
                    </div>
                  </template>
                </vue-autosuggest>
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-secondary"
                  class="btn-icon ml-50 flex-shrink-0"
                  :title="$t('Clear')"
                  :disabled="searchCustomer.length === 0"
                  @click="clearInput"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0 mr-1">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50 text-primary flex-shrink-0"
              />
              <span>{{ $t('Max interval one month') }}</span>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 8px;"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetFilters"
              >
                {{ $t('Restore Default') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                :disabled="state === ReportState.LOADING || !dateStart || !dateEnd"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="state === ReportState.LOADING"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <div
      v-if="!showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
      <b-badge
        v-if="periodLabel"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="CalendarIcon"
          size="12"
          class="mr-25"
        />
        {{ periodLabel }}
      </b-badge>
      <b-badge
        v-if="searchCustomer"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="UserIcon"
          size="12"
          class="mr-25"
        />
        {{ searchCustomer }}
      </b-badge>
      <b-link
        class="font-small-2 font-weight-bold ml-50 text-danger"
        @click="resetFilters"
      >
        {{ $t('Clear') }}
      </b-link>
    </div>

    <b-card class="shadow-sm border-0">
      <div class="table-container">
        <b-table
          ref="reportTable"
          :items="fetchUsageDetails"
          :busy="state === ReportState.LOADING"
          responsive
          :fields="visibleTableColumns"
          primary-key="id"
          show-empty
          :empty-text="emptyText"
          tbody-tr-class="excel-row"
          thead-tr-class="excel-header"
          table-class="excel-table"
          striped
          hover
          bordered
          small
          class="position-relative"
          @refreshed="onTableRefreshed"
        >
          <template #table-busy>
            <div class="text-center my-2 py-2">
              <b-spinner
                variant="primary"
                class="mb-1"
                :label="$t('Loading data')"
              />
              <div class="text-muted font-small-3">
                {{ $t('Loading data...') }}
              </div>
            </div>
          </template>

          <template
            v-for="field in visibleTableColumns"
            #[`cell(${field.key})`]="data"
          >
            <div
              :key="field.key"
              class="excel-cell"
              :class="getCellClass(field.key, data.value)"
            >
              {{ formatCellValue(field.key, data.value) }}
            </div>
          </template>
        </b-table>
      </div>

      <div class="mx-2 mb-1 mt-1">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t('Showing') }} {{ metadata.from }} {{ $t('to') }} {{ metadata.to }} {{ $t('of') }} {{ metadata.of }} {{ $t('entries') }}
            </span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="mt-1 d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BDropdown,
  BFormCheckbox,
  BFormCheckboxGroup,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BPagination,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue"
import store from "@/store"
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import {onUnmounted} from "@vue/composition-api"
import useUsageReport from "@/views/main/reports/useUsageReport"
import flatPickr from 'vue-flatpickr-component'
import {VueAutosuggest} from "vue-autosuggest"
import customerStoreModule from "@/views/main/customer/customerStoreModule"
import reportsStoreModule from "@/views/main/reports/reportsStoreModule"
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {ReportState} from "@/views/main/reports/utils"

const COLUMN_PREFERENCES_KEY = 'usage_report_column_preferences'

export default {
  computed: {
    ReportState() {
      return ReportState
    },
    visibleTableColumns() {
      return (this.tableColumns || []).filter(column =>
          this.selectedColumns.includes(column.key)
      )
    },
    availableColumns() {
      return (this.tableColumns || []).map(column => ({
        key: column.key,
        label: column.label || column.key
      }))
    },
    inputProps() {
      return {
        id: 'autosuggest__input',
        class: 'form-control',
        placeholder: this.$t('Customer'),
      }
    },
    activeFiltersCount() {
      const period = this.dateStart && this.dateEnd ? 1 : 0
      const customer = this.customerId ? 1 : 0
      return period + customer
    },
    periodLabel() {
      if (!this.dateStart || !this.dateEnd) return ''
      return `${this.dateStart} — ${this.dateEnd}`
    },
    periodPresets() {
      return [
        { id: 'today', label: this.$t('Today') },
        { id: 'yesterday', label: this.$t('Yesterday') },
        { id: 'last7', label: this.$t('Last 7 days') },
        { id: 'month', label: this.$t('This Month') },
      ]
    },
  },
  components: {
    BBadge,
    BDropdown,
    BButton,
    BCardBody,
    BFormCheckbox,
    BFormCheckboxGroup,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    BPagination,
    BTable,
    BCard,
    BRow,
    BCol,
    BCardHeader,
    BSpinner,
    flatPickr,
    VueAutosuggest,
    ToastificationContent,
  },
  data() {
    return {
      showFilters: false,
      dateStart: '',
      dateEnd: '',
      dateRangePreset: '',
      ignoreDateChange: false,
      emptyText: this.$t('No matching records found'),
      flatpickrConfig: {
        maxDate: new Date(),
        dateFormat: 'd/m/Y',
        locale: getFlatpickrLocale(),
      },
      searchCustomer: '',
      datasuggest: [],
      filteredOptions: [],
      isExporting: false,
      selectedColumns: []
    }
  },
  methods: {
    toDisplayDate(date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    syncDateRange() {
      if (!this.dateStart || !this.dateEnd) return
      this.dateRange = `${this.dateStart} to ${this.dateEnd}`
    },
    clampDateRange() {
      const start = this.dateFromString(this.dateStart)
      const end = this.dateFromString(this.dateEnd)
      if (!start || !end) {
        this.showFilters = true
        return false
      }

      let from = start
      let to = end
      if (from > to) {
        const swap = from
        from = to
        to = swap
      }

      const minAllowedDate = new Date(to)
      minAllowedDate.setDate(minAllowedDate.getDate() - 31)
      if (from < minAllowedDate) {
        this.showSnackbar({
          title: this.$t('Max interval one month'),
          icon: 'InfoIcon',
        })
        from = minAllowedDate
        this.showFilters = true
      }

      this.ignoreDateChange = true
      this.dateStart = this.toDisplayDate(from)
      this.dateEnd = this.toDisplayDate(to)
      this.syncDateRange()
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
      return true
    },
    setDefaultRange(refresh) {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - 1)
      this.ignoreDateChange = true
      this.dateStart = this.toDisplayDate(start)
      this.dateEnd = this.toDisplayDate(today)
      this.dateRangePreset = ''
      this.syncDateRange()
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
      if (refresh) this.updateReport()
    },
    setDatePreset(preset) {
      this.ignoreDateChange = true
      const today = new Date()
      let start = new Date(today)
      const end = new Date(today)

      if (preset === 'yesterday') {
        start.setDate(today.getDate() - 1)
        end.setDate(today.getDate() - 1)
      } else if (preset === 'last7') {
        start.setDate(today.getDate() - 6)
      } else if (preset === 'month') {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
      }

      this.dateStart = this.toDisplayDate(start)
      this.dateEnd = this.toDisplayDate(end)
      this.dateRangePreset = preset
      this.syncDateRange()
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
    },
    onCustomDateChange() {
      if (this.ignoreDateChange) return
      this.dateRangePreset = ''
      const start = this.dateFromString(this.dateStart)
      const end = this.dateFromString(this.dateEnd)
      if (!start || !end || start > end) return

      const minAllowedDate = new Date(end)
      minAllowedDate.setDate(minAllowedDate.getDate() - 31)
      if (start < minAllowedDate) {
        this.ignoreDateChange = true
        this.dateStart = this.toDisplayDate(minAllowedDate)
        this.showSnackbar({
          title: this.$t('Max interval one month'),
          icon: 'InfoIcon',
        })
        setTimeout(() => {
          this.ignoreDateChange = false
        }, 300)
      }
      this.syncDateRange()
    },
    applyFilters() {
      if (!this.clampDateRange()) return
      this.updateReport()
    },
    resetFilters() {
      const hadCustomer = !!this.customerId
      this.setDefaultRange(false)
      this.searchCustomer = ''
      this.filteredOptions = []
      this.customerId = ''
      if (!hadCustomer) this.updateReport()
    },
    selectHandler(option) {
      this.selected = option.item
      this.filteredOptions = []
      this.searchCustomer = option.item.name
      this.customerId = option.item.id
    },
    onInputChange(text) {
      if (text === '' || text === undefined || text.length < 3) {
        this.customerId = ''
        return
      }

      store.dispatch('app-customer/fetchCustomers', {
        q: this.searchCustomer,
        perPage: 5,
        page: 1,
      }).then(response => {
        this.filteredOptions = [{
          data: response.data.data,
        }]
      }).catch(() => {})
    },
    updateReport() {
      this.$refs.reportTable.refresh()
    },
    showSnackbar(config = {title: '', icon: '', variant: 'info'}) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: config.title,
          icon: config.icon,
          variant: config.variant,
        },
      })
    },
    dateFromString(string) {
      try {
        if (!string || typeof string !== 'string') return null

        const [date, month, year] = string.split("/")
        if (!date || !month || !year) return null

        const dateObj = new Date(year, month - 1, date)
        return isNaN(dateObj.getTime()) ? null : dateObj
      } catch (error) {
        return null
      }
    },
    formatDateRange(dateRange, type = "string") {
      try {
        if (!dateRange) return []

        let stringValues = []

        if (typeof dateRange === "string") {
          // Se contém " to ", é um range
          if (dateRange.includes(" to ")) {
            stringValues = dateRange.split(" to ")
          } else {
            // Se não contém " to ", é uma única data
            stringValues = [dateRange]
          }
        } else if (Array.isArray(dateRange)) {
          // Se é um array, mapear as datas
          stringValues = dateRange.map(date => {
            if (this.formatDate && typeof this.formatDate === 'function') {
              return this.formatDate(date)
            }
            // Fallback formatting
            if (date instanceof Date) {
              const day = date.getDate().toString().padStart(2, '0')
              const month = (date.getMonth() + 1).toString().padStart(2, '0')
              const year = date.getFullYear()
              return `${day}/${month}/${year}`
            }
            return date
          }).filter(date => date) // Remove valores vazios
        } else {
          // Se é uma única data (Date object)
          if (dateRange instanceof Date) {
            const formatted = this.formatDate ? this.formatDate(dateRange) : dateRange.toLocaleDateString(getIntlLocale())
            stringValues = [formatted]
          }
        }

        if (type === "string") return stringValues
        return stringValues.map(this.dateFromString).filter(date => date !== null)
      } catch (error) {
        return []
      }
    },
    async exportToExcel() {
      if (this.isExporting) return
      if (!this.clampDateRange()) return
      this.isExporting = true

      const formattedDates = this.formatDateRange(this.dateRange)

      // Correção: Se apenas uma data foi selecionada, usar a mesma data para início e fim
      const startDate = formattedDates[0]
      const endDate = formattedDates[1] || formattedDates[0] // Use a mesma data se não houver segunda data

      await store.dispatch('reports/exportUsageToExcel', {
        startDate: startDate,
        endDate: endDate,
        customerId: this.customerId,
        selectedColumns: this.selectedColumns,
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url

        // Correção no nome do arquivo também
        const dateRangeText = formattedDates.length > 1 && formattedDates[1]
            ? formattedDates.join(" - ")
            : formattedDates[0] // Se apenas uma data, mostrar apenas ela

        link.setAttribute('download', `${this.$t('Usage')} (${dateRangeText}).xls`)
        document.body.appendChild(link)
        link.click()
        this.isExporting = false
      }).catch(err => {
        this.isExporting = false

        this.showSnackbar({
          title: this.$t('Error exporting report'),
          icon: 'AlertTriangleIcon',
          variant: 'danger'
        })
      })
    },
    clearInput() {
      this.searchCustomer = ''
      this.onInputChange('')
    },
    selectAllColumns() {
      this.selectedColumns = (this.availableColumns || []).map(col => col.key)
      this.saveColumnPreferences()
    },
    deselectAllColumns() {
      this.selectedColumns = []
      this.saveColumnPreferences()
    },
    saveColumnPreferences() {
      try {
        const preferences = {
          selectedColumns: this.selectedColumns,
          timestamp: new Date().toISOString()
        }
        localStorage.setItem(COLUMN_PREFERENCES_KEY, JSON.stringify(preferences))
      } catch (_) {}
    },
    loadColumnPreferences() {
      try {
        const saved = localStorage.getItem(COLUMN_PREFERENCES_KEY)
        if (saved) {
          const preferences = JSON.parse(saved)
          if (preferences.selectedColumns && Array.isArray(preferences.selectedColumns)) {
            // Filter out any columns that no longer exist
            this.selectedColumns = preferences.selectedColumns.filter(colKey =>
                this.availableColumns.some(col => col.key === colKey)
            )
            return true
          }
        }
      } catch (_) {}
      return false
    },
    initializeColumnSelection() {
      const columns = this.availableColumns || []
      if (!columns.length) return

      // Try to load saved preferences
      const loaded = this.loadColumnPreferences()

      // If no saved preferences or empty selection, select all columns by default
      if (!loaded || this.selectedColumns.length === 0) {
        this.selectedColumns = columns.map(col => col.key)
      }
    },
    onTableRefreshed() {
    },
    formatCellValue(key, value) {
      // Add specific formatting for different column types
      if (value === null || value === undefined) {
        return '-'
      }

      // Add formatting logic based on column key
      if (key.includes('date') || key.includes('Date')) {
        return this.formatDateSafe(value)
      }

      if (key.includes('amount') || key.includes('price') || key.includes('cost')) {
        return this.formatCurrency(value)
      }

      return value
    },
    formatDateSafe(value) {
      try {
        if (!value) return '-'

        // If it's already a string in the desired format, return as is
        if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
          return value
        }

        // Try to convert to Date object
        let date
        if (value instanceof Date) {
          date = value
        } else if (typeof value === 'string') {
          // Handle different date string formats
          if (value.includes('T') || value.includes('-')) {
            date = new Date(value)
          } else {
            // Assume it's already in d/m/Y format
            return value
          }
        } else if (typeof value === 'number') {
          date = new Date(value)
        } else {
          return value.toString()
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
          return value.toString()
        }

        // Use the formatDate function from the composable if available
        if (this.formatDate && typeof this.formatDate === 'function') {
          return this.formatDate(date)
        }

        // Fallback formatting
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`

      } catch (error) {
        return value ? value.toString() : '-'
      }
    },
    getCellClass(key, value) {
      const classes = []

      // Add specific classes based on column type or value
      if (key.includes('amount') || key.includes('price') || key.includes('cost')) {
        classes.push('text-right')
      }

      if (key.includes('date') || key.includes('Date')) {
        classes.push('text-center')
      }

      if (value === null || value === undefined || value === '') {
        classes.push('text-muted')
      }

      return classes.join(' ')
    },
    formatCurrency(value) {
      try {
        if (value === null || value === undefined || value === '') return '-'

        const numValue = typeof value === 'string' ? parseFloat(value) : value
        if (isNaN(numValue)) return value.toString()

        return new Intl.NumberFormat(getIntlLocale(), {
          style: 'currency',
          currency: 'BRL'
        }).format(numValue)
      } catch (error) {
        return value ? value.toString() : '-'
      }
    }
  },
  watch: {
    showFilters(isOpen) {
      if (!isOpen) return
      this.ignoreDateChange = true
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
    },
    state: function (val, oldVal) {
      if (val === ReportState.FAILURE) {
        this.showSnackbar({
          title: this.$t('Error fetching report'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
      }
    },
    selectedColumns: {
      handler() {
        this.saveColumnPreferences()
      },
      deep: true
    },
    tableColumns: {
      handler() {
        if (this.tableColumns && this.tableColumns.length > 0) {
          this.initializeColumnSelection()
        }
      },
      immediate: true
    }
  },
  created() {
    this.setDefaultRange(false)
  },
  mounted() {
    // Initialize column selection after component is mounted
    this.$nextTick(() => {
      if (this.tableColumns && this.tableColumns.length > 0) {
        this.initializeColumnSelection()
      }
    })

    // Debug: Log initial state

    // Force table refresh if stuck in loading
    setTimeout(() => {
      if (this.state === ReportState.LOADING) {
        this.$refs.reportTable?.refresh()
      }
    }, 5000)
  },
  setup() {
    const REPORTS_STORE_MODULE_NAME = 'reports'
    const CUSTOMER_APP_STORE_MODULE_NAME = 'app-customer'

    if (!store.hasModule(REPORTS_STORE_MODULE_NAME)) store.registerModule(REPORTS_STORE_MODULE_NAME, reportsStoreModule)
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, customerStoreModule)

    onUnmounted(() => {
      if (store.hasModule(REPORTS_STORE_MODULE_NAME)) store.unregisterModule(REPORTS_STORE_MODULE_NAME)
      if (store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) store.unregisterModule(CUSTOMER_APP_STORE_MODULE_NAME)
    })

    const {
      fetchUsageDetails,
      dateRange,
      customerId,
      currentPage,
      perPage,
      state,
      reportTable,
      totalItems,
      tableColumns,
      formatDate,
      metadata
    } = useUsageReport()

    return {
      fetchUsageDetails,
      dateRange,
      customerId,
      currentPage,
      perPage,
      state,
      reportTable,
      totalItems,
      tableColumns,
      formatDate,
      metadata,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.usage-report-view {
  .spin-icon {
    animation: usage-report-spin 1s linear infinite;
  }

  .filters-card {
    border-radius: 0.5rem;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.18s ease;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@keyframes usage-report-spin {
  100% {
    transform: rotate(360deg);
  }
}

.auto-suggestions-box, .flat-picker {
  border-radius: 0.357rem 0 0 0.357rem;
}

// Column selector dropdown styles
.column-selector-menu {
  min-width: 280px;
  max-width: 320px;

  .column-options {
    max-height: 300px;
    overflow-y: auto;

    .custom-checkbox {
      .custom-control-label {
        .column-option-text {
          font-size: 0.875rem;
          line-height: 1.4;
        }
      }
    }
  }
}

// Excel-like table styling
.table-container {
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.excel-table {
  margin-bottom: 0;
  font-size: 0.875rem;

  // Header styling
  .excel-header {
    background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;

    th {
      font-weight: 600;
      text-align: center;
      vertical-align: middle;
      padding: 0.75rem 0.5rem;
      border-right: 1px solid #dee2e6;
      color: #495057;
      font-size: 0.8125rem;
      text-transform: uppercase;
      letter-spacing: 0.025em;

      &:last-child {
        border-right: none;
      }
    }
  }

  // Row styling
  .excel-row {
    &:hover {
      background-color: #f8f9fa;
    }

    td {
      padding: 0.5rem;
      border-right: 1px solid #e9ecef;
      vertical-align: middle;

      &:last-child {
        border-right: none;
      }
    }
  }

  // Striped rows
  tbody tr:nth-of-type(odd) {
    background-color: rgba(0, 0, 0, 0.02);
  }

  tbody tr:nth-of-type(even) {
    background-color: #ffffff;
  }
}

// Excel cell styling
.excel-cell {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 0.8125rem;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.text-right {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  &.text-center {
    text-align: center;
  }

  &.text-muted {
    color: #6c757d;
    font-style: italic;
  }
}

// Responsive table improvements
@media (max-width: 768px) {
  .excel-table {
    font-size: 0.75rem;

    .excel-header th {
      padding: 0.5rem 0.25rem;
      font-size: 0.75rem;
    }

    .excel-row td {
      padding: 0.375rem 0.25rem;
    }
  }

  .column-selector-menu {
    min-width: 260px;
    max-width: 280px;
  }
}

// Loading state improvements
.table-busy {
  .text-center {
    padding: 3rem 1rem;
  }
}

// Pagination styling
.pagination {
  .page-item {
    .page-link {
      color: #6c757d;
      border-color: #dee2e6;

      &:hover {
        color: #495057;
        background-color: #f8f9fa;
        border-color: #dee2e6;
      }
    }

    &.active .page-link {
      background-color: #007bff;
      border-color: #007bff;
    }
  }
}
</style>